/**
 * 搜索弹层:最近搜索 + 热门搜索的客户端交互
 *
 * 职责
 *   1. 把访客搜过的词存进 localStorage(提交搜索时记录,去重、最新在前);
 *   2. 渲染「最近搜索」列表(时钟图标 + 词 + 右侧删除 ×),支持单条删除和 Clear 清空;
 *   3. 在合适的时机重绘 —— 主题用 morph() 把空状态换成搜索结果、再换回来,
 *      morph 会把 JS 渲染的列表清空,所以这里必须能自愈。
 *
 * 为什么"幂等 + 签名比对"
 *   重绘由 MutationObserver 驱动,而重绘本身又会改动 DOM → 不加保护就会自触发死循环。
 *   所以渲染前先比对"当前词表签名 + 子节点数",两者都没变就直接返回,绝不空转。
 *
 * 2026-10-11 优化(行为不变)
 *   a) MutationObserver 只盯搜索弹层容器,不再监听整个 document.body ——
 *      否则购物车抽屉、集合页筛选、懒加载区块的每一次 DOM 变动都会把回调叫醒;
 *   b) 存储不按"显示条数"截断(只去重 + 硬上限 20),渲染时才按主题设置截断 ——
 *      这样商家把条数从 5 改成 8,老访客的历史也会立刻显示 8 条,而且写入路径
 *      不再依赖 DOM 里是否存在列表节点(morph 期间模块可能不在文档里)。
 *
 * 热门搜索不在这里渲染:它由 snippets/search-suggestions.liquid 服务端输出,
 * 词表来自主题设置(搜索弹层 → 关键词)。
 */
(() => {
  const STORAGE_KEY = 'hoyotoys:recent-searches';
  const FALLBACK_LIMIT = 5;
  /** 存储上限:与"显示条数"解耦,留出余量,避免商家调大设置后老访客没有历史可用 */
  const MAX_STORED = 20;

  const CLOCK_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)"/>' +
    '<path d="M12 7.5V12l3.2 1.8" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  const REMOVE_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round"/>' +
    '</svg>';

  /** @returns {number} 页面上允许显示的历史条数(来自主题设置,渲染在列表的 data 属性上) */
  function limitOf(list) {
    const value = Number(list?.dataset?.recentLimit);
    return Number.isFinite(value) && value > 0 ? value : FALLBACK_LIMIT;
  }

  /**
   * 读取全部历史(去重、去空、最新在前、硬上限 MAX_STORED)。
   * 注意:这里**不**按显示条数截断 —— 截断发生在渲染阶段。
   * @returns {string[]}
   */
  function readAll() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];

      const seen = new Set();
      const terms = [];
      for (const entry of parsed) {
        if (typeof entry !== 'string') continue;
        const term = entry.trim();
        if (!term) continue;
        const key = term.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        terms.push(term);
        if (terms.length >= MAX_STORED) break;
      }
      return terms;
    } catch {
      return [];
    }
  }

  function writeTerms(terms) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(terms.slice(0, MAX_STORED)));
    } catch {
      /* 隐私模式 / 存储被禁用:静默降级,不影响搜索本身 */
    }
  }

  /** 记录一次搜索(最新在前,同名去重)。不读 DOM,因此在 morph 期间也能正常工作 */
  function remember(rawTerm) {
    const term = String(rawTerm || '').trim();
    if (!term) return;

    const terms = readAll().filter((existing) => existing.toLowerCase() !== term.toLowerCase());
    terms.unshift(term);
    writeTerms(terms);
    render();
  }

  function removeTerm(term) {
    const key = String(term || '').toLowerCase();
    writeTerms(readAll().filter((existing) => existing.toLowerCase() !== key));
    render();
  }

  /** 用 textContent 组装条目:词来自访客输入,绝不能用 innerHTML 拼接 */
  function buildRecentItem(term, searchUrl, removeLabel) {
    const item = document.createElement('li');
    item.className = 'search-suggestions__recent-item';

    const link = document.createElement('a');
    link.className = 'search-suggestions__recent-link';
    link.href = `${searchUrl}?q=${encodeURIComponent(term)}`;

    const icon = document.createElement('span');
    icon.className = 'svg-wrapper search-suggestions__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = CLOCK_SVG;

    const label = document.createElement('span');
    label.className = 'search-suggestions__label search-suggestions__term';
    label.textContent = term;

    link.append(icon, label);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'search-suggestions__remove button-unstyled';
    remove.setAttribute('data-recent-remove', term);
    remove.setAttribute('aria-label', `${removeLabel}: ${term}`);
    remove.innerHTML = REMOVE_SVG;

    item.append(link, remove);
    return item;
  }

  function render() {
    const root = document.querySelector('[data-search-suggestions]');
    const list = root?.querySelector('[data-recent-list]');
    if (!root || !list) return;

    const block = root.querySelector('[data-recent-block]');
    const terms = readAll().slice(0, limitOf(list));
    const signature = terms.join('\u0000');

    // 幂等保护:词表和子节点数都没变就不动 DOM(否则会被自己的 MutationObserver 反复触发)
    if (list.dataset.renderedSignature === signature && list.childElementCount === terms.length) return;
    list.dataset.renderedSignature = signature;

    list.replaceChildren();
    if (terms.length === 0) {
      if (block) block.hidden = true;
      return;
    }

    const searchUrl = root.dataset.searchUrl || '/search';
    const removeLabel = root.dataset.removeLabel || 'Remove';
    const fragment = document.createDocumentFragment();
    for (const term of terms) fragment.append(buildRecentItem(term, searchUrl, removeLabel));
    list.append(fragment);
    if (block) block.hidden = false;
  }

  let frameRequested = false;
  function scheduleRender() {
    if (frameRequested) return;
    frameRequested = true;
    requestAnimationFrame(() => {
      frameRequested = false;
      render();
    });
  }

  // 记录搜索词:弹层表单和 /search 页表单都带 name="q",统一在捕获阶段处理
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const input = form.querySelector('input[name="q"], input[type="search"]');
      if (input instanceof HTMLInputElement) remember(input.value);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const removeButton = target.closest('[data-recent-remove]');
    if (removeButton) {
      event.preventDefault();
      removeTerm(removeButton.getAttribute('data-recent-remove'));
      return;
    }

    if (target.closest('[data-recent-clear]')) {
      event.preventDefault();
      writeTerms([]);
      render();
    }
  });

  // 打开弹层 / 输入关键词:兜底重绘(幂等,空转成本可忽略)
  document.addEventListener('focusin', (event) => {
    if (event.target instanceof Element && event.target.closest('dialog')) scheduleRender();
  });

  document.addEventListener('input', (event) => {
    if (event.target instanceof Element && event.target.matches('input[type="search"], .search-input')) scheduleRender();
  });

  // 主题用 morph() 换结果时会在搜索弹层内部产生 DOM 变动 —— 只盯这个容器。
  // 曾经监听整个 document.body:购物车抽屉、集合页筛选、懒加载区块的变动都会把
  // 回调叫醒,虽然有空转保护,但频率白高一个数量级。(2026-10-11 收窄)
  const observerTarget =
    document.querySelector('predictive-search-component') ?? document.querySelector('dialog') ?? document.body;
  new MutationObserver(scheduleRender).observe(observerTarget, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render);
  } else {
    render();
  }
})();
